import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'site-modules/shared/hooks/use-debounce';

/**
 * Custom hook to manage horizontal scrolling with drag support and arrow indicators
 *
 * @param {Object} options - Configuration options
 * @param {React.RefObject} options.containerRef - Ref object pointing to the scrollable container element
 * @param {boolean} options.enableDrag - Enable drag-to-scroll functionality (default: true)
 * @param {number} options.arrowDebounce - Debounce time for arrow visibility updates in ms (default: 50)
 * @param {number} options.scrollThreshold - Threshold for showing arrows in px (default: 1)
 * @param {number} options.dragThreshold - Minimum drag distance to prevent click events in px (default: 5)
 * @returns {Object} Scroll management utilities
 * @property {boolean} showLeftArrow - Whether to show left arrow indicator
 * @property {boolean} showRightArrow - Whether to show right arrow indicator
 * @property {Function} scrollToStart - Function to scroll to the start of container
 * @property {Function} scrollToEnd - Function to scroll to the end of container
 * @property {Function} scrollToCenter - Function to scroll element to center
 * @property {Function} handleDragStart - Event handler for drag start
 * @property {Function} handleScroll - Event handler for scroll events
 */
export function useHorizontalScroll({
  enableDrag = true,
  arrowDebounce = 50,
  scrollThreshold = 1,
  dragThreshold = 5,
  containerRef = null,
} = {}) {
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const dragStateRef = useRef({ isDragging: false, startX: 0 });
  const resizeObserverRef = useRef(null);
  const dragTimeoutRef = useRef(null);

  /**
   * Updates arrow visibility based on scroll position
   */
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- stable identity is required by the debounced observer callback.
  const updateArrows = useCallback(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;

    setShowLeftArrow(scrollLeft > scrollThreshold);
    setShowRightArrow(scrollWidth - clientWidth - scrollLeft > scrollThreshold);
  }, [containerRef, scrollThreshold]);
  const debouncedUpdateArrows = useDebounce(updateArrows, arrowDebounce);

  /**
   * Initialize arrows with ResizeObserver for container size changes
   * ResizeObserver handles both content changes and viewport resizes
   */
  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    // Use requestAnimationFrame for accurate initial measurement after layout
    const rafId = requestAnimationFrame(() => {
      updateArrows();
    });

    // Watch for container size changes (content updates, CSS changes, viewport resize)
    resizeObserverRef.current = new ResizeObserver(() => {
      debouncedUpdateArrows();
    });
    resizeObserverRef.current.observe(container);

    return () => {
      cancelAnimationFrame(rafId);

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      debouncedUpdateArrows.cancel();
    };
  }, [updateArrows, debouncedUpdateArrows, containerRef]);

  /**
   * Cleanup drag timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    };
  }, []);

  const handleDragStart = useCallback(
    startEvent => {
      if (!enableDrag || !containerRef.current) {
        return;
      }

      // Ignore multi-touch gestures (pinch-zoom, etc.)
      if (startEvent.touches && startEvent.touches.length > 1) {
        return;
      }

      const container = containerRef.current;
      const eventData = startEvent.touches?.[0] ?? startEvent;
      const startX = eventData.pageX - container.offsetLeft;
      const startScrollLeft = container.scrollLeft;

      dragStateRef.current = {
        isDragging: false,
        startX,
      };

      const onDragMove = moveEvent => {
        // Check if container still exists (could be removed during drag)
        if (!containerRef.current) {
          return;
        }

        const moveEventData = moveEvent.touches?.[0] ?? moveEvent;
        const currentX = moveEventData.pageX - container.offsetLeft;
        const walk = currentX - startX;

        // Only start dragging if moved beyond threshold to prevent accidental drags
        if (!dragStateRef.current.isDragging && Math.abs(walk) > dragThreshold) {
          dragStateRef.current.isDragging = true;
        }

        if (dragStateRef.current.isDragging) {
          container.scrollLeft = startScrollLeft - walk;

          debouncedUpdateArrows();

          // Prevent default to stop text selection and page scrolling
          if (moveEvent.cancelable) {
            moveEvent.preventDefault();
          }
        }
      };

      const onDragEnd = () => {
        if (!containerRef.current) {
          return;
        }

        container.removeEventListener('mousemove', onDragMove);
        container.removeEventListener('touchmove', onDragMove);
        container.removeEventListener('mouseleave', onDragEnd);
        container.removeEventListener('mouseup', onDragEnd);
        container.removeEventListener('touchend', onDragEnd);
        container.removeEventListener('touchcancel', onDragEnd);

        // Reset drag state after event loop completes
        dragTimeoutRef.current = setTimeout(() => {
          dragStateRef.current.isDragging = false;
          dragTimeoutRef.current = null;
        }, 0);
      };

      container.addEventListener('mousemove', onDragMove);
      container.addEventListener('touchmove', onDragMove, { passive: false });
      container.addEventListener('mouseleave', onDragEnd);
      container.addEventListener('mouseup', onDragEnd);
      container.addEventListener('touchend', onDragEnd);
      container.addEventListener('touchcancel', onDragEnd);

      // Prevent text selection on mouse drag
      if (!startEvent.touches) {
        startEvent.preventDefault();
      }
    },
    [enableDrag, containerRef, dragThreshold, debouncedUpdateArrows]
  );

  const scrollToStart = () => {
    if (containerRef.current) {
      // eslint-disable-next-line no-param-reassign
      containerRef.current.scrollLeft = 0;
      debouncedUpdateArrows();
    }
  };

  const scrollToEnd = () => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    container.scrollLeft = container.scrollWidth - container.clientWidth;
    debouncedUpdateArrows();
  };

  const scrollToCenter = element => {
    const container = containerRef.current;

    if (!container || !element) {
      return;
    }

    const centerStart = container.clientWidth / 2 - element.clientWidth / 2;

    container.scrollLeft = Math.max(0, element.offsetLeft - centerStart);
    debouncedUpdateArrows();
  };

  return {
    showLeftArrow,
    showRightArrow,
    scrollToStart,
    scrollToEnd,
    scrollToCenter,
    handleDragStart,
    handleScroll: debouncedUpdateArrows,
  };
}
